import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import moment from 'moment';
import {
  EndUserType,
  NIGHTWEB_APP_CONSTANT,
  NODATA_MESSAGES,
  STATUS_CODES,
  USER_DETAIL_COLUMN,
  USER_STATUS,
  API_CONSTANTS,
  MESSAGE_CONSTANTS,
} from 'src/app/constants';
import {
  ThreatPair,
  ThreatPairUser,
} from 'src/app/components/common/threat-pair-table/threat-pair-table.model';
import {
  DeviceService,
  DownloadService,
  OrganizationService,
  ThreatPairService,
  ThreatPairZoneRadiiMeters,
  UserService,
  NotificationService,
  ToasterService,
} from 'src/app/services';
import {
  DateUtility,
  GeoPoint,
  NightwebUtil,
  bearingDegrees,
  compassDirection,
  haversineDistanceMeters,
  runSimulation,
  stopSimulation,
  initializeDraggableSimulation,
  sendSingleSimulationCheckIn,
  setSimulationActivity,
  getSimulationCheckInTimes,
  getEnvironmentData,
  getSimulationUserIds,
  SimulationActivityType,
  SimulationCheckInTimes,
  SimulationProgress,
  getDistanceToMeter,
  getDistanceParameterLabel,
  getDistanceParameterData,
  survivorDevId,
  perpetratorDevId,
  survivorQaId,
  perpetratorQaId,
  formatSignalIconHtml,
  startLocalSimulation,
  stopLocalSimulation,
  LocalSimulationAlertPayload,
} from 'src/app/utils';
import {
  GoogleMapComponent,
  GoogleMapParty,
  GoogleMapPartyDragEvent,
  GoogleMapZoneConfig,
} from 'src/app/components/common/google-map/google-map.component';
import type { AdvocateLogMessage } from 'src/app/components/common/proximity-simulation-overlay/proximity-simulation-overlay.component';
import { TableConfig } from 'src/app/components/common/interface';
import {
  EndUserProfileInformationComponent,
  NotesComponent,
  PerpetratorSidebarComponent,
  SettingsSidebarComponent,
  ThreatPairAlertLogComponent,
} from 'src/app/components';
import {
  ConfigureColumnModalComponent,
  ConfigurationModalComponent,
  AudioRequestModalComponent,
  CustomSimulationModalComponent,
  CustomSimulationModalResult,
  DownloadDeviceLogsModalComponent,
} from 'src/app/components/modals';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import {
  AxisLinkZoneState,
  ProximityTrend,
  ThreatPairPartySummary,
} from 'src/app/components/threat-pair-live-map-page/threat-pair-live-map-page.component';

type ViewMode = 'table' | 'map';

const SURVIVOR_HEADING_COLOR = '#0d6efd';
/** Perpetrator marker when inside the yellow (or red) monitoring zone. */
const PERPETRATOR_INSIDE_ZONE_COLOR = '#dc3545';
/** Perpetrator marker when outside the yellow monitoring zone. */
const PERPETRATOR_OUTSIDE_ZONE_COLOR = '#ffc107';
/**
 * GPS accuracy isn't modeled anywhere in this codebase yet — see the same constant's doc comment
 * in `threat-pair-live-map-page.component.ts`, whose map view this page's map view mirrors.
 */
const MOCK_GPS_ACCURACY_LABEL = '±4 m';
const EVENT_LOG_MAX_ENTRIES = 20;
/** Keep enough fixes for FR-402 arrow (≥2) + fading trail (≤12). */
const LOCATION_HISTORY_MAX = 12;

/**
 * Duplicated from `threat-pair-live-map-page.component.ts` rather than imported: this page embeds
 * the same map view in place (toggled, not routed), and that component's `ngOnInit` resolves
 * `endUserId`/`threatPairId` from the *route snapshot* — it has no `@Input`s to drive it from a
 * parent instead, and this page's own route has no `:threatPairId` segment at all. Editing that
 * file to add inputs is out of scope here (this page must not change the existing live-map
 * route/page in any way), so the map/zone logic below is a parameterized copy of its private
 * methods, driven by this component's own `userId`/`selectedPair` instead of route params.
 */
const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#212529' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212529' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#a9a9b3' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#c9c9d1' }],
  },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#7c7c86' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1c2a1f' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38383f' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212529' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8b8b94' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4a4a53' }] },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#212529' }],
  },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2c2c33' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
];

const LIGHT_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9d9e8' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

@Component({
  selector: 'app-end-user-detail-v2',
  templateUrl: './end-user-detail-v2.component.html',
  styleUrls: ['./end-user-detail-v2.component.scss'],
  standalone: false,
})
export class EndUserDetailV2Component implements OnInit, OnDestroy {
  @ViewChild(GoogleMapComponent) googleMapRef?: GoogleMapComponent;
  @ViewChild('liveMapCard') liveMapCard?: ElementRef<HTMLElement>;
  @ViewChild(EndUserProfileInformationComponent)
  endUserProfileInformationComponent!: EndUserProfileInformationComponent;
  @ViewChild(NotesComponent) notesComponent!: NotesComponent;
  @ViewChild(PerpetratorSidebarComponent)
  perpetratorSidebarComponent!: PerpetratorSidebarComponent;
  @ViewChild(SettingsSidebarComponent)
  settingsSidebarComponent!: SettingsSidebarComponent;
  @ViewChild('threatPairAlertLog') threatPairAlertLogPanel?: ThreatPairAlertLogComponent;

  userId!: string;
  user: any = null;
  userInitials = '';
  loading = false;
  private loadingMore = false;
  errorMessage: string | null = null;

  viewMode: ViewMode = 'map';

  readonly userStatus = USER_STATUS;
  readonly endUserTypeList = EndUserType;
  readonly notFoundMessage = NODATA_MESSAGES.NO_USER_ACTIVITIES;

  // --- Table view state (ported verbatim from UserDetailComponent — same data-loading,
  // date-range filtering, pagination, and per-org column customization). ---
  userDetailForm!: FormGroup;
  tableConfig: TableConfig = {
    headerElements: USER_DETAIL_COLUMN.filter((column) => column.enable).map(
      (column) => column.headerName,
    ),
    bodyElements: USER_DETAIL_COLUMN.filter((column) => column.enable).map(
      (column) => column.bodyElement,
    ),
    tableDatas: [],
  };
  userColumnData = {};
  /** The logged-in operator's own id — for their personal column preferences, distinct from `userId` (the end user being viewed). */
  operatorId!: string;
  audioRequestModal!: MdbModalRef<AudioRequestModalComponent>;
  configureColumnComponent!: MdbModalRef<ConfigureColumnModalComponent>;
  configurationModalConfig!: MdbModalRef<ConfigurationModalComponent>;
  customSimulationModal!: MdbModalRef<CustomSimulationModalComponent>;
  readonly config = NIGHTWEB_APP_CONSTANT.modalConfig;
  readonly mdbDateFormat = NIGHTWEB_APP_CONSTANT.mdbDefaultDateFormat;
  versionOneWatchEliminatedColumns = [
    { label: 'Packet Sequence', value: 'packetSequence' },
  ];
  private userCheckinData: any[] = [];
  private totalDataCount = 0;
  checkInTimeInterval: number = 0;

  // --- Notes ---
  notes: any[] = [];
  isLoadingNotes = false;

  // --- Map view state (see the constants' doc comment above for why this is a parameterized
  // copy of ThreatPairLiveMapPageComponent rather than a shared/imported implementation). ---
  selectedPair: ThreatPair | null = null;
  //canShowMapToggle = true;

  isFullscreen = false;
  isDarkMode = false;
  showLatestCheckIn = false;
  readonly darkMapStyles = DARK_MAP_STYLES;
  readonly lightMapStyles = LIGHT_MAP_STYLES;
  readonly accuracyLabel = MOCK_GPS_ACCURACY_LABEL;

  mapParties: GoogleMapParty[] = [];
  survivorSummary: ThreatPairPartySummary | null = null;
  perpetratorSummary: ThreatPairPartySummary | null = null;

  distanceMeters: number | null = null;
  distanceLabel = '';
  proximityTrend: ProximityTrend = 'unknown';
  closingRateMps: number | null = null;
  isHoldingStaleFix = false;

  zoneConfig: GoogleMapZoneConfig | null = null;
  zoneState: AxisLinkZoneState = 'unknown';

  eventLog: AdvocateLogMessage[] = [];
  isSimulationRunning = false;
  isDraggableSimulationRunning = false;
  isLocalSimulationRunning = false;
  localSimulationAlert: LocalSimulationAlertPayload | null = null;
  localSimulationLog: AdvocateLogMessage | null = null;
  simulationActivity: SimulationActivityType = 'UNKNOWN';
  simulationCheckInTimes: SimulationCheckInTimes = {
    survivorSeconds: null,
    perpetratorSeconds: null,
  };
  readonly simulationUserIds = getSimulationUserIds();
  simulationProgress: SimulationProgress = {
    completedCheckIns: 0,
    totalCheckIns: 0,
    remainingCheckIns: 0,
  };
  private simulationGeneration = 0;
  activeCustomSimulationResult: CustomSimulationModalResult | null = null;

  get isSimulationCompleted(): boolean {
    return (
      this.isSimulationRunning &&
      this.simulationProgress.totalCheckIns > 0 &&
      this.simulationProgress.completedCheckIns >= this.simulationProgress.totalCheckIns
    );
  }

  get canDragMarkers(): boolean {
    return (
      this.displaySimulationButton &&
      this.isSimulationRunning &&
      this.isDraggableSimulationRunning
    );
  }

  private previousDistanceMeters: number | null = null;
  private refreshHandle: ReturnType<typeof setInterval> | undefined;

  private survivorLocationData: { count: number; rows: any[] } | null = null;
  private perpetratorLocationData: { count: number; rows: any[] } | null = null;

  displaySimulationButton = true;
  isLoading = {
    removeEmergencyCadence: false,
    gpsCheck: false,
    networkCheck: false,
    testAction: false,
    downloadLogs: false,
    downloadRrmsLogs: false,
  };
  isLoggedInEndUser: boolean = this.nightwebUtil.isEndUser();
  loggedInUser!: any;
  device = {
    gps: { status: '', date: '' },
    network: { status: '', date: '' },
  };
  deviceId: string | null = null;
  testEndTime: string | null = null;
  remainingTimeInMilliseconds = 0;
  currentTime = '';
  private compareTestTimeInterval: ReturnType<typeof setInterval> | undefined;
  isSuperAdmin: boolean = this.nightwebUtil.isSuperAdmin();

  mapFeatures: any;
  emergencyTriggeredLocations: any = {
    indexes: [],
    icon: '',
  };
  simulationScenarioTitle = '';
  isPerpetrator = false;
  watchSocketStatus = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private threatPairService: ThreatPairService,
    private organizationService: OrganizationService,
    private modalService: MdbModalService,
    private nightwebUtil: NightwebUtil,
    private dateUtil: DateUtility,
    private _notificationService: NotificationService,
    private _toasterService: ToasterService,
    private _deviceService: DeviceService,
    private _downloadService: DownloadService,
  ) { }

  ngOnInit(): void {
    const survivorId = getEnvironmentData() === 'Dev' ? survivorDevId : survivorQaId;
    const perpetratorId = getEnvironmentData() === 'Dev' ? perpetratorDevId : perpetratorQaId;
    this.userId = this.route.snapshot.paramMap.get('id') ?? '';
    if (this.userId === survivorId || this.userId === perpetratorId) {
      this.displaySimulationButton = true;
    } else {
      this.displaySimulationButton = false;
    }
    if (!this.userId) {
      this.errorMessage = 'This end user could not be found.';
      return;
    }
    this.setuserDetailForm();
    this.fetchUser();
    this.getNotes(this.userId);
    this._notificationService
      .listen('new-check-in-data')
      .subscribe((response: any) => {
        const userId = this.router.url.replace(
          `${API_CONSTANTS.END_USERS_V2}/`,
          '',
        );
        if (response && userId === response.userId) {
          if (this.user && response.data?.isEmergencyState != null) {
            this.user.emergencyCadence = !!response.data.isEmergencyState;
          }
          const index = this.tableConfig.tableDatas.findIndex(
            ({ packetSequence }) =>
              packetSequence === response.data.packetSequence,
          );

          const row = {
            ...response.data,
            latLong: this.concatLatLong(response.data.gps),
          };
          if (index > -1) {
            this.tableConfig.tableDatas[index] = row;
            const checkInIndex = this.userCheckinData.findIndex(
              ({ packetSequence }) =>
                packetSequence === response.data.packetSequence,
            );
            if (checkInIndex > -1) {
              this.userCheckinData[checkInIndex] = row;
            }
          } else {
            this.userCheckinData = [row, ...this.userCheckinData];
            this.tableConfig.tableDatas = [row, ...this.tableConfig.tableDatas];
          }
        }
      });

    this._notificationService
      .listen('new-check-in-location')
      .subscribe((response: any) => {
        const parties = response?.data;
        this.implementGoogleLocation(parties);
      });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopUserSimulation();
    if (this.compareTestTimeInterval) {
      clearInterval(this.compareTestTimeInterval);
    }
    if (
      document.fullscreenElement &&
      document.fullscreenElement === this.liveMapCard?.nativeElement
    ) {
      void document.exitFullscreen?.();
    }
  }

  goBack(): void {
    this.router.navigate(['/end-users']);
  }

  setViewMode(mode: ViewMode): void {
    if (mode !== 'map' && this.isFullscreen) {
      void document.exitFullscreen?.();
    }
    this.viewMode = mode;
  }

  /** Same as `UserDetailComponent.onThreatPairAlerts` — opens the shared alert-log side panel. */
  onThreatPairAlerts(pair: ThreatPair | null, distanceParameter: string = 'meter'): void {
    if (pair) {
      this.threatPairAlertLogPanel?.openAlerts(pair, distanceParameter);
    }
  }

  toggleFullscreen(): void {
    const el = this.liveMapCard?.nativeElement;
    if (!el) {
      return;
    }

    if (!document.fullscreenElement) {
      void el.requestFullscreen?.();
      return;
    }

    if (document.fullscreenElement === el) {
      void document.exitFullscreen?.();
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const el = this.liveMapCard?.nativeElement;
    this.isFullscreen = !!el && document.fullscreenElement === el;
    setTimeout(() => this.googleMapRef?.triggerResize(), 100);
  }

  toggleLatestCheckIn(): void {
    this.showLatestCheckIn = !this.showLatestCheckIn;
  }

  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
  }

  /**
   * Whichever party of the selected threat pair is *not* this page's own end user — the header
   * above already identifies the current end user, so only their counterpart needs a card.
   */
  get counterpartRole(): 'Survivor' | 'Perpetrator' {
    return this.user?.userType === this.endUserTypeList.Perpetrator
      ? 'Survivor'
      : 'Perpetrator';
  }

  get counterpartUser(): ThreatPairUser | undefined {
    return this.counterpartRole === 'Survivor'
      ? this.selectedPair?.survivor
      : this.selectedPair?.perpetrator;
  }

  get zoneRadiiMeters(): ThreatPairZoneRadiiMeters {
    const redDistanceThreshold = getDistanceToMeter(this.selectedPair?.redDistanceThreshold ?? NIGHTWEB_APP_CONSTANT.axisLinkFallbackRedZoneRadiusMeters, this.selectedPair?.distanceParameter ?? 'meter');
    const yellowDistanceThreshold = getDistanceToMeter(this.selectedPair?.distanceThreshold ?? NIGHTWEB_APP_CONSTANT.axisLinkFallbackYellowZoneRadiusMeters, this.selectedPair?.distanceParameter ?? 'meter');
    return {
      red: redDistanceThreshold,
      yellow: yellowDistanceThreshold,
    };
  }

  get zoneRadiiLabel(): { yellow: string; red: string } {
    const radii = this.zoneRadiiMeters;
    return {
      yellow: `${Math.round(radii.yellow)} m`,
      red: `${Math.round(radii.red)} m`,
    };
  }

  get statusPillLabel(): string {
    switch (this.zoneState) {
      case 'red':
        return 'ALERT';
      case 'yellow':
        return 'CAUTION';
      case 'outside':
        return 'SAFE';
      default:
        return 'UNKNOWN';
    }
  }

  get statusPillClass(): string {
    switch (this.zoneState) {
      case 'red':
        return 'live-map-status-red';
      case 'yellow':
        return 'live-map-status-yellow';
      case 'outside':
        return 'live-map-status-outside';
      default:
        return 'live-map-status-unknown';
    }
  }

  get isZoneCentreStale(): boolean {
    return !!this.survivorSummary?.hasLocation && !!this.survivorSummary?.isStale;
  }

  get isInAlertZone(): boolean {
    return this.zoneState === 'red' || this.zoneState === 'yellow';
  }

  get distanceDisplayLabel(): string {
    return this.isHoldingStaleFix ? `${this.distanceLabel} (held)` : this.distanceLabel;
  }

  /** Latest check-in for this page's end user (`userId`), not the threat-pair counterpart. */
  get latestCheckIn(): any | null {
    return this.tableConfig.tableDatas?.[0] ?? this.userCheckinData?.[0] ?? null;
  }

  get latestCheckInGpsLabel(): string {
    const checkIn = this.latestCheckIn;
    if (!checkIn) {
      return '—';
    }
    return checkIn.latLong || this.concatLatLong(checkIn.gps) || '—';
  }

  get latestCheckInHeartRateLabel(): string {
    const value = this.latestCheckIn?.heartRate;
    return value != null && value !== '' ? `${value} bpm` : '—';
  }

  get latestCheckInSignalHtml(): string {
    return formatSignalIconHtml(this.latestCheckIn, '—');
  }

  get latestCheckInWristLabel(): string {
    const value = this.latestCheckIn?.isOnWrist;
    if (value == null) {
      return '—';
    }
    return value ? 'Yes' : 'No';
  }

  get latestCheckInBatteryLabel(): string {
    const value = this.latestCheckIn?.batteryStatus;
    return value != null && value !== '' ? `${value}%` : '—';
  }

  get latestCheckInChargingLabel(): string {
    const value = this.latestCheckIn?.isCharging;
    if (value == null) {
      return '—';
    }
    return value ? 'Yes' : 'No';
  }

  get latestCheckInTimestampLabel(): string {
    const checkIn = this.latestCheckIn;
    if (!checkIn) {
      return '—';
    }
    return this.formatRowTimestamp(checkIn);
  }

  get closingRateLabel(): string {
    if (this.closingRateMps == null) {
      return '—';
    }
    const sign = this.closingRateMps > 0 ? '+' : '';
    return `${sign}${this.closingRateMps.toFixed(1)} m/s`;
  }

  get mapBannerTitle(): string {
    const name = this.perpetratorSummary?.name ?? 'Monitored subject';
    const zone = this.zoneState === 'red' ? 'Red Zone' : 'Yellow Zone';
    return `${name} within ${zone}`;
  }

  get mapBannerDetail(): string {
    const parts = [`${this.distanceDisplayLabel} away`];
    if (this.perpetratorSummary?.headingLabel) {
      parts.push(`heading ${this.perpetratorSummary.headingLabel}`);
    }
    parts.push(this.proximityTrendLabel());
    return parts.join(' · ');
  }

  proximityTrendLabel(): string {
    switch (this.proximityTrend) {
      case 'closing':
        return 'Toward you';
      case 'opening':
        return 'Moving away';
      case 'steady':
        return 'Holding steady';
      default:
        return 'Not enough data yet';
    }
  }

  get proximityTrendClass(): string {
    switch (this.proximityTrend) {
      case 'closing':
        return 'text-danger';
      case 'opening':
        return 'text-success';
      case 'steady':
        return 'text-warning';
      default:
        return '';
    }
  }

  private fetchUser(): void {
    this.loading = true;
    this.userService
      .getUserById(this.userId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          this.user = response?.data;
          this.checkInTimeInterval =
            this.user?.configurations?.find(
              (config: any) => config.code === 'CHECK_IN_TIME',
            )?.value ?? 0;
          this.userInitials = this.nightwebUtil.getInitials(this.user?.demographic);
          this.deviceId = this.user?.activeDevice?.deviceId ?? null;
          if (this.deviceId && !this.isLoggedInEndUser) {
            this.getDeviceGps();
            this.getDeviceNetwork();
          }
          this.applyTestEndState(
            this.user?.expireTime ? this.user.expiredUtcDate : null,
          );
          this.resolveSelectedPair();
          if (this.isLoggedInEndUser) {
            this.isPerpetrator = this.user?.userType === EndUserType.Perpetrator;
          }
          if (this.deviceId) {
            this._deviceService.getWatchSocketStatus(this.deviceId ?? '').subscribe((response: any) => {
              this.watchSocketStatus = response.data.status;
            });
          }
          // Needs `this.user` (watchAppVersion/organizationId) — must run after it's set above,
          // not in parallel with this fetch the way `UserDetailComponent`'s `forkJoin` does it.
          this.loadInitialUserActivities();
        },
        error: () => {
          this.errorMessage = 'Unable to load this end user right now.';
        },
      });
  }

  private resolveSelectedPair(): void {
    const pairs: ThreatPair[] = this.user?.threatPairs ?? [];
    this.selectedPair =
      pairs.find((pair) => pair.statusKey === 'active') ?? pairs[0] ?? null;
    this.survivorLocationData = null;
    this.perpetratorLocationData = null;
    if (!this.selectedPair) {
      this.mapParties = [];
      this.getDefaultLocation(this.userId);
    }
    /*const role = this.nightwebUtil.getRole();
    this.canShowMapToggle = !!this.selectedPair; 

    if (this.selectedPair) {
      this.startPolling();
    } else if (this.viewMode === 'map') {
      this.viewMode = 'table';
    }*/
  }

  // --- Table view: data loading (ported verbatim from UserDetailComponent) ---

  private setuserDetailForm(): void {
    this.userDetailForm = this.formBuilder.group({
      keyword: [],
      limit: [25],
      offset: [NIGHTWEB_APP_CONSTANT.defaultOffset],
      createdFrom: [],
      createdTo: [],
    });
  }

  private setUserDetailColumnData(data: any): void {
    if (data) {
      USER_DETAIL_COLUMN.map((item: any) => {
        if (data[item?.dataName] !== undefined) {
          item.enable = data[item?.dataName];
        }
      });
    }
    this.userColumnData = USER_DETAIL_COLUMN.reduce((acc: any, item: any) => {
      acc[item.dataName] = item.enable;
      return acc;
    }, {});
    this.tableConfig.headerElements = USER_DETAIL_COLUMN.filter(
      (column) => column.enable,
    ).map((column) => column.headerName);
    this.tableConfig.bodyElements = USER_DETAIL_COLUMN.filter(
      (column) => column.enable,
    ).map((column) => column.bodyElement);
  }

  private loadInitialUserActivities(): void {
    this.loading = true;
    this.userService
      .getUserActivities(this.userId, {
        createdTo: moment(new Date()).format(NIGHTWEB_APP_CONSTANT.defaultMomentDateFormat),
        limit: 25,
        offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (checkInData: any) => {
          if (this.user?.watchAppVersion == 1) {
            this.tableConfig.headerElements = this.tableConfig.headerElements.filter(
              (header) =>
                !this.versionOneWatchEliminatedColumns.some((item) => item.label === header),
            );
            this.tableConfig.bodyElements = this.tableConfig.bodyElements.filter(
              (body: any) =>
                !this.versionOneWatchEliminatedColumns.some((item) => item.value === body.value),
            );
          }
          this.totalDataCount = checkInData.data.count;
          this.userCheckinData = checkInData.data.rows.map((checkIn: any) => ({
            ...checkIn,
            latLong: this.concatLatLong(checkIn?.gps),
          }));
          this.tableConfig.tableDatas = [...this.userCheckinData];

          this.operatorId = this.nightwebUtil.getLoginUserId();
          this.organizationService
            .getConfigureColumnInfo(this.operatorId, this.user?.organizationId)
            .subscribe((response: any) => {
              if (response.data.userDetailColumn) {
                this.setUserDetailColumnData(response.data.userDetailColumn);
              } else {
                this.setUserDetailColumnData('');
              }
            });
        },
      });

    if (this.selectedPair) {
      this.userService
        .getThreatPairLocations(this.userId)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: (response: any) => {
            this.implementGoogleLocation(response.data);
          },
          error: () => {
            this.errorMessage = 'Unable to load this end user right now.';
          },
        });
    }
  }

  getUserActivities(filter: any): void {
    const isFirstPage = filter.offset === NIGHTWEB_APP_CONSTANT.defaultOffset;
    this.loading = isFirstPage;
    this.loadingMore = !isFirstPage;
    if (isFirstPage) {
      this.tableConfig.tableDatas = [];
    }
    this.userService
      .getUserActivities(this.userId, filter)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingMore = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          this.totalDataCount = response.data.count;
          this.userCheckinData = isFirstPage
            ? response.data.rows
            : [...this.userCheckinData, ...response.data.rows];
          this.tableConfig.tableDatas = this.userCheckinData.map((checkIn: any) => ({
            ...checkIn,
            latLong: this.concatLatLong(checkIn?.gps),
          }));
        },
      });
  }

  checkDateIsBefore(): boolean {
    return this.dateUtil.checkIsBefore(
      this.userDetailForm.controls['createdFrom']?.value,
      this.userDetailForm.controls['createdTo']?.value,
    );
  }

  getMaxDate(): Date {
    return this.dateUtil.getMaxCreatedFromDate();
  }

  getMinDate(): Date {
    return this.dateUtil.getMinCreatedToDate(this.userDetailForm.controls['createdFrom']?.value);
  }

  getFiltered(isRefreshed: boolean = false, dateSelected: boolean = false): void {
    if (isRefreshed) {
      this.userDetailForm.controls['offset'].setValue(NIGHTWEB_APP_CONSTANT.defaultOffset);
      if (!dateSelected) {
        this.userDetailForm.patchValue({ limit: 25, createdFrom: null, createdTo: null });
      }
    }
    const filterParams: any = this.nightwebUtil.getFilterParams(this.userDetailForm.value);
    if (filterParams?.createdFrom) {
      filterParams.createdFrom = this.dateUtil.convertToMMddYYYY(filterParams.createdFrom);
    }
    if (filterParams?.createdTo) {
      filterParams.createdTo = this.dateUtil.convertToMMddYYYY(filterParams.createdTo);
    } else {
      filterParams.createdTo = this.dateUtil.convertToMMddYYYY(new Date());
    }
    this.getUserActivities(filterParams);
  }

  clearDatePicked(type: string): void {
    this.userDetailForm.controls[type].setValue(null);
    this.getFiltered();
  }

  openConfigureColumnModal(column?: any): void {
    const config = this.nightwebUtil.shallowCopy(this.config);
    config.data.title = 'Configure Column';
    config.data.formData = column;
    config.modalClass = 'modal-dialog-scrollable modal-md';
    this.configureColumnComponent = this.modalService.open(ConfigureColumnModalComponent, config);
    this.configureColumnComponent.onClose.subscribe((data: any) => {
      if (data) {
        const updateData = { id: this.operatorId, userDetailColumn: data };
        this.organizationService.updateAdminUserDetailColumn(updateData).subscribe();
        this.setUserDetailColumnData(data);
      }
    });
  }

  openEditConfigurationModal(): void {
    if (!this.user?.activeDevice?.deviceId) {
      return;
    }
    const config = this.nightwebUtil.shallowCopy(this.config);
    config.data.title = 'Configuration';
    config.modalClass = 'modal-dialog-scrollable modal-md';
    config.data.formData = this.user;
    config.data.formData.mode = 'edit';
    config.data.formData.module = 'user';
    config.data.formData.userId = this.user.id;
    config.data.formData.deviceId = this.user.activeDevice.deviceId;
    config.data.confirmButtonLabel = 'Update';
    this.configurationModalConfig = this.modalService.open(
      ConfigurationModalComponent,
      config,
    );
    this.configurationModalConfig.onClose.subscribe((data: any) => {
      if (data) {
        this.fetchUser();
      }
    });
  }

  scrollToBottom(event: any): void {
    if (
      !event?.scrolledToBottom ||
      this.loading ||
      this.loadingMore ||
      this.userCheckinData.length >= this.totalDataCount
    ) {
      return;
    }

    this.userDetailForm.controls['offset'].setValue(
      this.userDetailForm.value.offset + 1,
    );
    this.getFiltered();
  }

  receiveEvent(_event: any): void { }

  private concatLatLong(gps: any): string {
    if (gps?.latitude && gps?.longitude && gps.latitude !== 'null' && gps.longitude !== 'null') {
      return gps.latitude + ', ' + gps.longitude;
    }
    return '';
  }

  // --- Notes (ported from UserDetailComponent.getNotes) ---

  private getNotes(id: string): void {
    this.isLoadingNotes = true;
    this.userService
      .getNotes(id)
      .pipe(finalize(() => (this.isLoadingNotes = false)))
      .subscribe({
        next: (response: any) => {
          if (response.statusCode == STATUS_CODES.OK) {
            this.notes = response.data.rows;
          }
        },
      });
  }

  // --- Map view: polling/zone logic (ported from ThreatPairLiveMapPageComponent — see the file
  // header's doc comment for why this is a copy rather than a shared implementation) ---

  private startPolling(): void {
    this.loadLocations();
    this.stopPolling();
    this.refreshHandle = setInterval(
      () => this.loadLocations(true),
      NIGHTWEB_APP_CONSTANT.threatPairLiveMapRefreshMs,
    );
  }

  private stopPolling(): void {
    if (this.refreshHandle) {
      clearInterval(this.refreshHandle);
      this.refreshHandle = undefined;
    }
  }

  private getPartyId(user: ThreatPairUser | undefined): string | undefined {
    return (user as any)?._id ?? (user as any)?.id ?? undefined;
  }

  private loadLocations(isRefresh = false): void {
    const survivorId = this.getPartyId(this.selectedPair?.survivor);
    const perpetratorId = this.getPartyId(this.selectedPair?.perpetrator);

    if (!survivorId || !perpetratorId) {
      return;
    }

    const zoneRadiiMeters = this.zoneRadiiMeters;

    this.userService
      .getLocations(survivorId)
      .pipe(
        switchMap((rawSurvivorResponse: any) => {
          const anchorPosition = this.extractLatestPosition(rawSurvivorResponse?.data);
          return this.threatPairService.getSurvivorLocations(survivorId, anchorPosition);
        }),
        switchMap((survivorResponse: any) => {
          const referencePosition = this.extractLatestPosition(survivorResponse?.data);
          return this.threatPairService
            .getPerpetratorLocations(perpetratorId, referencePosition, zoneRadiiMeters)
            .pipe(
              map((perpetratorResponse: any) => ({
                survivorResponse,
                perpetratorResponse,
              })),
            );
        }),
      )
      .subscribe({
        next: ({ survivorResponse, perpetratorResponse }: any) => {
          this.applyLocations(survivorResponse?.data, perpetratorResponse?.data, zoneRadiiMeters);
        },
        error: () => {
          void isRefresh;
        },
      });
  }

  private applyLocations(
    survivorData: any,
    perpetratorData: any,
    zoneRadiiMeters: ThreatPairZoneRadiiMeters,
  ): void {
    this.survivorSummary = this.buildPartySummary(
      'Survivor',
      this.selectedPair?.survivor?.name ?? 'Survivor',
      survivorData,
    );
    this.perpetratorSummary = this.buildPartySummary(
      'Perpetrator',
      this.selectedPair?.perpetrator?.name ?? 'Perpetrator',
      perpetratorData,
    );

    if (!this.survivorSummary?.position || !this.perpetratorSummary?.position) {
      this.distanceMeters = null;
      this.distanceLabel = '';
      this.proximityTrend = 'unknown';
      this.closingRateMps = null;
      this.zoneState = 'unknown';
      this.isHoldingStaleFix = false;
      this.previousDistanceMeters = null;
      this.setMapParties(survivorData, perpetratorData);
      return;
    }

    const isStaleFix = this.survivorSummary.isStale || this.perpetratorSummary.isStale;
    if (isStaleFix) {
      this.isHoldingStaleFix = this.distanceMeters != null;
      this.setMapParties(survivorData, perpetratorData);
      return;
    }
    this.isHoldingStaleFix = false;

    const currentDistance = haversineDistanceMeters(
      this.survivorSummary.position,
      this.perpetratorSummary.position,
    );
    this.distanceMeters = currentDistance;
    const distanceParameterLabel = getDistanceParameterLabel(this.selectedPair?.distanceParameter ?? 'meter');
    const currentDistanceData = getDistanceParameterData(currentDistance, this.selectedPair?.distanceParameter ?? 'meter');
    this.distanceLabel = `${Math.round(currentDistanceData)} ${distanceParameterLabel}`;

    // Always draw zone circles (red, yellow, and buffer) around the victim (survivor)
    this.zoneConfig = this.survivorSummary?.position
      ? {
        center: this.survivorSummary.position,
        yellowRadiusMeters: zoneRadiiMeters.yellow,
        redRadiusMeters: zoneRadiiMeters.red,
        whiteRadiusMeters: zoneRadiiMeters.yellow * 2,
      }
      : null;

    const previousZoneState = this.zoneState;
    this.zoneState = this.classifyZone(currentDistance, this.zoneState, zoneRadiiMeters);
    if (this.zoneState !== previousZoneState) {
      this.logZoneTransition(previousZoneState, this.zoneState, currentDistance);
    }

    this.setMapParties(survivorData, perpetratorData);

    if (this.previousDistanceMeters != null) {
      const closedMeters = this.previousDistanceMeters - currentDistance;
      const elapsedSeconds = NIGHTWEB_APP_CONSTANT.threatPairLiveMapRefreshMs / 1000;
      const rate = closedMeters / elapsedSeconds;
      this.closingRateMps = Math.round(rate * 10) / 10;

      const deadband = NIGHTWEB_APP_CONSTANT.axisLinkClosingRateDeadbandMps;
      this.proximityTrend =
        rate > deadband ? 'closing' : rate < -deadband ? 'opening' : 'steady';
    }
    this.previousDistanceMeters = currentDistance;
  }

  private setMapParties(survivorData: any, perpetratorData: any): void {
    this.mapParties = [
      {
        label: this.survivorSummary?.name ?? 'Survivor',
        features: survivorData,
        color: SURVIVOR_HEADING_COLOR,
        partyType: 'SURVIVOR',
      },
      {
        label: this.perpetratorSummary?.name ?? 'Perpetrator',
        features: perpetratorData,
        color: this.perpetratorMarkerColor,
        partyType: 'PERPETRATOR',
      },
    ].filter((party) => party.features?.rows?.length);
  }

  /** Yellow outside the yellow zone; red when inside yellow or red. */
  private get perpetratorMarkerColor(): string {
    return this.zoneState === 'yellow' || this.zoneState === 'red'
      ? PERPETRATOR_INSIDE_ZONE_COLOR
      : PERPETRATOR_OUTSIDE_ZONE_COLOR;
  }

  private logZoneTransition(
    from: AxisLinkZoneState,
    to: AxisLinkZoneState,
    distanceMeters: number,
  ): void {
    if (from === 'unknown') {
      return;
    }
    const perpName = this.perpetratorSummary?.name ?? 'Monitored subject';
    const distanceParameterLabel = getDistanceParameterLabel(this.selectedPair?.distanceParameter ?? 'meter');
    const distanceData = getDistanceParameterData(distanceMeters, this.selectedPair?.distanceParameter ?? 'meter');
    const distanceLabel = `~${Math.round(distanceData)} ${distanceParameterLabel}`;
    const zoneLabel: Record<AxisLinkZoneState, string> = {
      red: 'Red Zone',
      yellow: 'Yellow Zone',
      outside: 'Outside Monitoring Zone',
      unknown: 'Unknown',
    };

    const line =
      to === 'outside'
        ? `${perpName} returned to ${zoneLabel[to]} (${distanceLabel})`
        : `${perpName} entered ${zoneLabel[to]} (${distanceLabel})`;

    this.eventLog = [
      { message: line, receivedAt: new Date(), zone: zoneLabel[to] },
      ...this.eventLog,
    ].slice(0, EVENT_LOG_MAX_ENTRIES);
  }

  private classifyZone(
    distanceMeters: number,
    previous: AxisLinkZoneState,
    radii: ThreatPairZoneRadiiMeters,
  ): AxisLinkZoneState {
    const fraction = NIGHTWEB_APP_CONSTANT.axisLinkZoneHysteresisBufferFraction;
    const redBuffer = Math.max(
      radii.red * fraction,
      NIGHTWEB_APP_CONSTANT.axisLinkZoneHysteresisMinMeters,
    );
    const yellowBuffer = Math.max(
      radii.yellow * fraction,
      NIGHTWEB_APP_CONSTANT.axisLinkZoneHysteresisMinMeters,
    );

    if (previous === 'red') {
      if (distanceMeters > radii.red + redBuffer) {
        return distanceMeters <= radii.yellow + yellowBuffer ? 'yellow' : 'outside';
      }
      return 'red';
    }

    if (previous === 'yellow') {
      if (distanceMeters <= radii.red) {
        return 'red';
      }
      return distanceMeters > radii.yellow + yellowBuffer ? 'outside' : 'yellow';
    }

    if (distanceMeters <= radii.red) {
      return 'red';
    }
    return distanceMeters <= radii.yellow ? 'yellow' : 'outside';
  }

  private extractLatestPosition(data: any): GeoPoint | null {
    const rows = (data?.rows ?? []).filter(
      (row: any) =>
        row?.gps?.latitude &&
        row.gps.latitude !== 'null' &&
        row?.gps?.longitude &&
        row.gps.longitude !== 'null',
    );
    if (!rows.length) {
      return null;
    }
    const latest = rows[rows.length - 1];
    return {
      lat: parseFloat(latest.gps.latitude),
      lng: parseFloat(latest.gps.longitude),
    };
  }

  private buildPartySummary(
    role: 'Survivor' | 'Perpetrator',
    name: string,
    data: any,
  ): ThreatPairPartySummary {
    const rows = (data?.rows ?? []).filter(
      (row: any) =>
        row?.gps?.latitude &&
        row.gps.latitude !== 'null' &&
        row?.gps?.longitude &&
        row.gps.longitude !== 'null',
    );

    if (!rows.length) {
      return {
        role,
        name,
        position: null,
        headingLabel: null,
        headingDegrees: null,
        reportingLabel: 'Unavailable',
        isHighFrequency: false,
        lastUpdatedLabel: '—',
        hasLocation: false,
        isStale: true,
      };
    }

    const latest = rows[rows.length - 1];
    const previous = rows.length > 1 ? rows[rows.length - 2] : null;

    const position: GeoPoint = {
      lat: parseFloat(latest.gps.latitude),
      lng: parseFloat(latest.gps.longitude),
    };
    const previousPosition: GeoPoint | null = previous
      ? {
        lat: parseFloat(previous.gps.latitude),
        lng: parseFloat(previous.gps.longitude),
      }
      : null;

    const headingDegrees = previousPosition
      ? bearingDegrees(previousPosition, position)
      : null;

    const isHighFrequency = !!latest.isEmergencyState;

    return {
      role,
      name,
      position,
      headingLabel: headingDegrees != null ? compassDirection(headingDegrees) : null,
      headingDegrees,
      reportingLabel: isHighFrequency ? 'High-frequency (emergency mode)' : 'Standard',
      isHighFrequency,
      lastUpdatedLabel: this.formatRowTimestamp(latest),
      hasLocation: true,
      isStale: this.isRowStale(latest),
    };
  }

  private parseRowTimestamp(row: any): Date | null {
    const raw =
      row?.createdAt ??
      row?.recordedAt ??
      row?.checkInTime ??
      row?.timestamp ??
      row?.date ??
      row?.updatedAt;
    if (!raw) {
      return null;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatRowTimestamp(row: any): string {
    const parsed = this.parseRowTimestamp(row);
    if (!parsed) {
      return '—';
    }
    return parsed.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private isRowStale(row: any): boolean {
    const parsed = this.parseRowTimestamp(row);
    if (!parsed) {
      return true;
    }
    return (
      Date.now() - parsed.getTime() > NIGHTWEB_APP_CONSTANT.axisLinkFallbackPositionStalenessMs
    );
  }

  private toLocationFeatures(
    locations: Array<{ latitude: number; longitude: number; checkInTime: string }> | undefined,
  ): { count: number; rows: any[] } {
    const rows = [...(locations ?? [])]
      .filter((loc) => loc?.latitude != null && loc?.longitude != null)
      .sort(
        (a, b) =>
          new Date(a.checkInTime).getTime() - new Date(b.checkInTime).getTime(),
      )
      .map((loc) => ({
        gps: {
          latitude: String(loc.latitude),
          longitude: String(loc.longitude),
        },
        checkInTime: loc.checkInTime,
        isEmergencyState: false,
      }));

    return { count: rows.length, rows };
  }

  /**
   * Merge newly arrived fixes onto prior history so velocity arrows still have
   * ≥2 points when each socket/API payload only includes the latest location.
   */
  private mergeLocationFeatures(
    previous: { count: number; rows: any[] } | null,
    incoming: { count: number; rows: any[] },
  ): { count: number; rows: any[] } {
    if (!incoming?.rows?.length) {
      return previous ?? { count: 0, rows: [] };
    }
    if (!previous?.rows?.length) {
      return {
        count: Math.min(incoming.rows.length, LOCATION_HISTORY_MAX),
        rows: incoming.rows.slice(-LOCATION_HISTORY_MAX),
      };
    }

    const byKey = new Map<string, any>();
    const keyOf = (row: any) =>
      `${row.gps?.latitude},${row.gps?.longitude},${row.checkInTime ?? ''}`;

    for (const row of previous.rows) {
      byKey.set(keyOf(row), row);
    }
    for (const row of incoming.rows) {
      byKey.set(keyOf(row), row);
    }

    const rows = [...byKey.values()]
      .sort(
        (a, b) =>
          new Date(a.checkInTime).getTime() - new Date(b.checkInTime).getTime(),
      )
      .slice(-LOCATION_HISTORY_MAX);

    return { count: rows.length, rows };
  }

  private implementGoogleLocation(parties: any): void {
    if (!Array.isArray(parties) || !this.selectedPair) {
      return;
    }

    const threatPairId = (this.selectedPair as any)._id ?? (this.selectedPair as any).id;
    const forThisPair = parties.filter(
      (p: any) => p.threatPairId === threatPairId,
    );
    if (!forThisPair.length) {
      return;
    }

    const survivorEntry = forThisPair.find((p: any) => p.type === 'SURVIVOR');
    const perpetratorEntry = forThisPair.find((p: any) => p.type === 'PERPETRATOR');

    const survivorIncoming = survivorEntry
      ? this.toLocationFeatures(survivorEntry.locations)
      : null;
    const perpetratorIncoming = perpetratorEntry
      ? this.toLocationFeatures(perpetratorEntry.locations)
      : null;

    const survivorData = survivorIncoming
      ? this.mergeLocationFeatures(this.survivorLocationData, survivorIncoming)
      : this.survivorLocationData;
    const perpetratorData = perpetratorIncoming
      ? this.mergeLocationFeatures(this.perpetratorLocationData, perpetratorIncoming)
      : this.perpetratorLocationData;

    this.survivorLocationData = survivorData;
    this.perpetratorLocationData = perpetratorData;

    this.applyLocations(survivorData, perpetratorData, this.zoneRadiiMeters);
  }

  openSimulationModal(): void {
    if (this.isSimulationRunning) {
      return;
    }

    const config = this.nightwebUtil.shallowCopy(this.config);
    config.data.title = 'Run Simulation';
    config.modalClass = 'modal-dialog-centered modal-md simulation-modal-dialog';
    this.customSimulationModal = this.modalService.open(
      CustomSimulationModalComponent,
      config,
    );
    this.customSimulationModal.onClose.subscribe(
      (result: CustomSimulationModalResult | undefined) => {
        if (result?.survivor && result?.perpetrator) {
          this.enterSimulationFullscreen();
          void this.startCustomSimulation(result);
        }
      },
    );
  }

  private enterSimulationFullscreen(): void {
    const mapElement = this.liveMapCard?.nativeElement;
    const fullscreenRequest = mapElement?.requestFullscreen?.();
    if (fullscreenRequest) {
      void fullscreenRequest.catch(() => undefined);
    }
  }

  editSimulation(): void {
    if (this.isSimulationRunning) {
      this.stopUserSimulation();
    }
    this.openSimulationModal();
  }

  private async startCustomSimulation(
    result: CustomSimulationModalResult,
  ): Promise<void> {
    if (this.isSimulationRunning) {
      return;
    }
    this.activeCustomSimulationResult = result;
    // Fresh history so prior runs don't poison heading/arrow for the new scenario.
    this.survivorLocationData = null;
    this.perpetratorLocationData = null;
    const generation = ++this.simulationGeneration;
    this.isSimulationRunning = true;
    this.simulationScenarioTitle = result.scenarioTitle;
    const scenarioActivity: SimulationActivityType = result.activity ?? 'UNKNOWN';
    this.simulationActivity = scenarioActivity;
    this.simulationCheckInTimes = { survivorSeconds: null, perpetratorSeconds: null };
    setSimulationActivity(scenarioActivity);

    if (result.scenarioId === 'full-scenario-local') {
      this.isDraggableSimulationRunning = false;
      this.isLocalSimulationRunning = true;
      this.localSimulationLog = null;
      this.localSimulationAlert = null;
      const initialCount = result.perpetrator?.length || 108;
      this.simulationProgress = {
        completedCheckIns: 0,
        totalCheckIns: initialCount,
        remainingCheckIns: initialCount,
      };
      this.simulationCheckInTimes = { survivorSeconds: 30, perpetratorSeconds: 30 };

      const threatPairId = (this.selectedPair as any)._id ?? (this.selectedPair as any).id;
      const survivorName = this.selectedPair?.survivor?.name || 'Survivor';
      const perpetratorName = this.selectedPair?.perpetrator?.name || 'Perpetrator';

      startLocalSimulation({
        survivorName,
        perpetratorName,
        survivorLocations: result.survivor,
        perpetratorLocations: result.perpetrator,
        yellowThresholdMeters: this.zoneRadiiMeters.yellow,
        redThresholdMeters: this.zoneRadiiMeters.red,
        bufferThresholdMeters: this.zoneRadiiMeters.yellow * 2,
        distanceParameter: this.selectedPair?.distanceParameter || 'yard',
        intervalMs: 1000,
        onStep: (step, progress) => {
          if (this.simulationGeneration !== generation) {
            return;
          }
          this.simulationProgress = {
            completedCheckIns: progress.completedCheckIns,
            totalCheckIns: progress.totalCheckIns,
            remainingCheckIns: progress.remainingCheckIns,
          };
          this.simulationCheckInTimes = {
            survivorSeconds: step.survivorCheckInTimeSeconds ?? 30,
            perpetratorSeconds: step.perpetratorCheckInTimeSeconds ?? 30,
          };
          this.simulationActivity = step.activity;

          // Push new coordinates to map
          const nowIso = new Date().toISOString();
          this.implementGoogleLocation([
            {
              threatPairId,
              type: 'SURVIVOR',
              locations: [
                {
                  latitude: step.survivor[1],
                  longitude: step.survivor[0],
                  checkInTime: nowIso,
                },
              ],
            },
            {
              threatPairId,
              type: 'PERPETRATOR',
              locations: [
                {
                  latitude: step.perpetrator[1],
                  longitude: step.perpetrator[0],
                  checkInTime: nowIso,
                },
              ],
            },
          ]);

          // Feed local watch alerts and advocate logs to overlay
          if (step.clearAlerts) {
            this.localSimulationAlert = null;
          } else if (step.alert) {
            this.localSimulationAlert = step.alert;
          }

          if (step.advocateLog) {
            this.localSimulationLog = { ...step.advocateLog, receivedAt: new Date() };
          }
        },
        onComplete: () => {
          // Keep overlay and watch state visible so user can inspect final All Clear state until clicking Stop
        },
      });
      return;
    }

    if (result.scenarioId === 'custom-draggable') {
      this.isDraggableSimulationRunning = true;
      this.isLocalSimulationRunning = false;
      this.simulationProgress = {
        completedCheckIns: 0,
        totalCheckIns: 0,
        remainingCheckIns: 0,
      };
      try {
        await initializeDraggableSimulation();
        // stopSimulation inside initialize resets activity — restore scenario selection.
        setSimulationActivity(this.simulationActivity);
        // Send initial check-in for survivor and perpetrator to establish initial positions on map
        if (result.survivor?.length) {
          const firstSurvivor = result.survivor[0];
          await sendSingleSimulationCheckIn(
            'Survivor',
            [firstSurvivor[0], firstSurvivor[1]],
            this.simulationActivity,
          );
          this.syncSimulationCheckInTimes();
        }
        if (result.perpetrator?.length) {
          const firstPerp = result.perpetrator[0];
          await sendSingleSimulationCheckIn(
            'Perpetrator',
            [firstPerp[0], firstPerp[1]],
            this.simulationActivity,
          );
          this.syncSimulationCheckInTimes();
        }
        this.simulationProgress.completedCheckIns = 2;
      } catch (err) {
        console.error('Failed to initialize draggable simulation:', err);
      }
      return;
    }

    this.isDraggableSimulationRunning = false;
    this.isLocalSimulationRunning = false;
    try {
      await runSimulation({
        survivor: result.survivor,
        perpetrator: result.perpetrator,
        activity: this.simulationActivity,
        onProgress: (progress) => {
          this.simulationProgress = progress;
          if (progress.checkInTimes) {
            this.simulationCheckInTimes = progress.checkInTimes;
          } else {
            this.syncSimulationCheckInTimes();
          }
        },
      });
    } finally {
      if (this.simulationGeneration === generation) {
        this.isSimulationRunning = false;
      }
    }
  }

  async onPartyDragEnd(event: GoogleMapPartyDragEvent): Promise<void> {
    if (!this.canDragMarkers) {
      return;
    }
    const partyName = event.partyType === 'SURVIVOR' ? 'Survivor' : 'Perpetrator';
    try {
      await sendSingleSimulationCheckIn(partyName, [event.lng, event.lat], this.simulationActivity);
      this.syncSimulationCheckInTimes();
      this.simulationProgress.completedCheckIns++;
    } catch (err) {
      console.error(`Failed to send check-in on drag end for ${partyName}:`, err);
    }
  }

  onSimulationActivityChange(activity: SimulationActivityType): void {
    this.simulationActivity = activity;
    setSimulationActivity(activity);
  }

  private syncSimulationCheckInTimes(): void {
    this.simulationCheckInTimes = getSimulationCheckInTimes();
  }

  stopUserSimulation(): void {
    this.simulationGeneration++;
    stopSimulation();
    stopLocalSimulation();
    this.isSimulationRunning = false;
    this.isDraggableSimulationRunning = false;
    this.isLocalSimulationRunning = false;
    this.localSimulationAlert = null;
    this.localSimulationLog = null;
    this.simulationActivity = 'UNKNOWN';
    this.simulationCheckInTimes = { survivorSeconds: null, perpetratorSeconds: null };
    this.simulationScenarioTitle = '';
    this.simulationProgress = {
      completedCheckIns: 0,
      totalCheckIns: 0,
      remainingCheckIns: 0,
    };
    if (document.fullscreenElement === this.liveMapCard?.nativeElement) {
      void document.exitFullscreen?.();
    }
  }

  restartUserSimulation(): void {
    if (!this.activeCustomSimulationResult) {
      return;
    }
    const savedResult = this.activeCustomSimulationResult;
    this.stopUserSimulation();
    this.activeCustomSimulationResult = savedResult;
    setTimeout(() => {
      this.enterSimulationFullscreen();
      void this.startCustomSimulation(savedResult);
    }, 50);
  }

  onRemoveCadence() {
    this.isLoading.removeEmergencyCadence = true;
    this.userService
      .removeCadence(this.userId)
      .pipe(
        finalize(() => {
          this.isLoading.removeEmergencyCadence = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.statusCode == STATUS_CODES.OK) {
            this.user.emergencyCadence = false;
            this._toasterService.show(
              'SUCCESS',
              MESSAGE_CONSTANTS.REMOVE_EMERGENCY_CADENCE_SUCCESS,
            );
          }
        },
        error: (error) =>
          console.log('error removing emergency cadence', error),
      });
  }

  onRequestRecording() {
    this.loggedInUser = this.nightwebUtil.decodeToken(
      this.nightwebUtil.getLoggedInUser(),
    );
    const requestData = {
      advocateName: this.loggedInUser?.name,
      enduserName: this.user.demographic.fullName,
      accountNumber: this.user.accountNumber,
      advocateEmail: this.loggedInUser?.userName,
      advocatePhone: this.loggedInUser?.phone,
    };

    const config = this.nightwebUtil.shallowCopy(this.config);
    config.data.title = 'Request Audio Recording';
    config.data.formData = requestData;
    config.modalClass = 'modal-dialog-scrollable modal-md';
    this.audioRequestModal = this.modalService.open(
      AudioRequestModalComponent,
      config,
    );
  }

  getDeviceGps(isPinged: boolean = false) {
    if (!this.deviceId) {
      return;
    }
    this._deviceService
      .getDeviceGps(this.deviceId)
      .pipe(
        finalize(() => {
          this.isLoading.gpsCheck = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.statusCode == STATUS_CODES.OK) {
            this.device.gps = {
              ...response.data,
              date:
                response?.data?.date !== 'N/A'
                  ? moment(response.data.date).format('MM/DD/yyyy hh:mm a')
                  : 'N/A',
            };
            isPinged &&
              this._toasterService.show(
                'SUCCESS',
                MESSAGE_CONSTANTS.CHECK_GPS_SUCCESS,
              );
          }
        },
      });
  }

  getDeviceNetwork(isPinged: boolean = false) {
    if (!this.deviceId) {
      return;
    }
    this._deviceService
      .getDeviceNetwork(this.deviceId)
      .pipe(
        finalize(() => {
          this.isLoading.networkCheck = false;
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.statusCode == STATUS_CODES.OK) {
            this.device.network = {
              ...response.data,
              date:
                response?.data?.date !== 'N/A'
                  ? moment(response.data.date).format('MM/DD/yyyy hh:mm a')
                  : 'N/A',
            };
            isPinged &&
              this._toasterService.show(
                'SUCCESS',
                MESSAGE_CONSTANTS.CHECK_NETWORK_SUCCESS,
              );
          }
        },
      });
  }

  onGpsCheck() {
    if (!this.deviceId) {
      return;
    }
    this.isLoading.gpsCheck = true;
    this._deviceService.pingGps(this.deviceId).subscribe({
      next: (response) => {
        if (response.statusCode == STATUS_CODES.OK) {
          setTimeout(() => {
            this.getDeviceGps(true);
          }, 120000);
        }
      },
      error: (error) => console.log('error getting pinged gps', error),
    });
  }

  onNetworkCheck() {
    if (!this.deviceId) {
      return;
    }
    this.isLoading.networkCheck = true;
    this._deviceService.pingNetwork(this.deviceId).subscribe({
      next: (response) => {
        if (response.statusCode == STATUS_CODES.OK) {
          setTimeout(() => {
            this.getDeviceNetwork(true);
          }, 120000);
        }
      },
      error: (error) => console.log('error getting pinged network', error),
    });
  }

  onDownloadLogs() {
    if (!this.deviceId || this.isLoading.downloadLogs) {
      return;
    }
    const config = this.nightwebUtil.shallowCopy(this.config);
    config.modalClass = 'modal-dialog-scrollable modal-md';
    config.data.title = 'Download Device Logs';
    config.data.deviceId = this.deviceId;
    config.data.fileName =
      this.user?.activeDevice?.info?.imeiNumber || this.deviceId;
    // Keep the sidebar button disabled while the picker/download modal is open
    // so the user cannot open multiple download requests.
    this.isLoading.downloadLogs = true;
    const modalRef = this.modalService.open(
      DownloadDeviceLogsModalComponent,
      config,
    );
    modalRef.onClose.subscribe(() => {
      this.isLoading.downloadLogs = false;
    });
  }

  onDownloadRRMSLogs() {
    this.isLoading.downloadRrmsLogs = true;
    this._deviceService
      .getRRMSLogs(this.user.accountNumber)
      .pipe(finalize(() => (this.isLoading.downloadRrmsLogs = false)))
      .subscribe({
        next: (response) => {
          if (response.statusCode == STATUS_CODES.OK) {
            this._downloadService.downloadJson(
              response.data.rows,
              `RRMS-Logs_${this.user.accountNumber}`,
            );
          }
        },
        error: (error) => console.log('error getting RRMS logs', error),
      });
  }

  get canBeginTest(): boolean {
    return (
      this.user?.status?.toLowerCase() === 'activated' && !!this.user?.rapidSynced
    );
  }

  private applyTestEndState(expiredUtcDate: string | null | undefined): void {
    if (!expiredUtcDate) {
      this.testEndTime = null;
      this.remainingTimeInMilliseconds = 0;
      return;
    }
    const end = moment(this.dateUtil.convertUtcToLocal(expiredUtcDate));
    this.remainingTimeInMilliseconds = Math.max(0, end.diff(moment()));
    this.testEndTime =
      this.remainingTimeInMilliseconds > 0 ? end.format('hh:mm A') : null;
    if (this.testEndTime) {
      this.compareTestEndAndCurrentTime();
    }
  }

  compareTestEndAndCurrentTime() {
    if (this.compareTestTimeInterval) {
      clearInterval(this.compareTestTimeInterval);
    }
    this.compareTestTimeInterval = setInterval(() => {
      this.currentTime = moment().format('hh:mm A');
      if (this.currentTime === this.testEndTime) {
        clearInterval(this.compareTestTimeInterval);
        this.testEndTime = null;
        this.remainingTimeInMilliseconds = 0;
      }
    }, 1000);
  }

  beginTest() {
    this.isLoading.testAction = true;
    this.userService
      .beginTest(this.userId)
      .pipe(finalize(() => (this.isLoading.testAction = false)))
      .subscribe({
        next: (response) => {
          if (response.statusCode == STATUS_CODES.OK) {
            this.applyTestEndState(
              response.data.expireTime ? response.data.expiredUtcDate : null,
            );
            this._toasterService.show('SUCCESS', response.message);
          }
        },
        error: (error) => {
          console.log('error starting the test', error);
          this._toasterService.showError(error);
        },
      });
  }

  onStopTest() {
    this.isLoading.testAction = true;
    this.userService
      .stopTest(this.userId)
      .pipe(finalize(() => (this.isLoading.testAction = false)))
      .subscribe({
        next: (response) => {
          if (response.statusCode == STATUS_CODES.OK) {
            clearInterval(this.compareTestTimeInterval);
            this.testEndTime = null;
            this.remainingTimeInMilliseconds = 0;
            this._toasterService.show('SUCCESS', response.message);
          }
        },
        error: (error) => {
          console.log('error stopping the test', error);
          this._toasterService.showError(error);
        },
      });
  }

  getDefaultLocation(userId: string) {
    this.userService
      .getLocations(userId)
      .pipe()
      .subscribe((response: any) => {
        if (response.statusCode == STATUS_CODES.OK) {
          this.mapFeatures = response.data;
          this.emergencyTriggeredLocations.indexes =
            this.mapFeatures.rows.reduce(
              (accumulator: number[], current: any, index: number) => {
                if (current.isEmergencyState) {
                  accumulator.push(index);
                }
                return accumulator;
              },
              [],
            );
          this.emergencyTriggeredLocations.indexes.length &&
            (this.emergencyTriggeredLocations.icon =
              '../../../../assets/img/png/red-marker.png');
        }
      });
  }

  displayColor(data: any): boolean {
    /*const diffSeconds = Math.abs(
      moment(data.createdAt).diff(moment(data.checkInTime), 'seconds'),
    );
    const definedCheckInTime = Math.floor(this.checkInTimeInterval / 1000);
    return diffSeconds >= definedCheckInTime + 300;
    */
   let isOfflineData: boolean = false;
   if (data.isDelayed) {
    isOfflineData = data.isDelayed;
   }
   return isOfflineData;
  }

  getActivityRowClass = (data: any, field?: any): Record<string, boolean> => ({
    'text-warning':
      this.displayColor(data) && field?.value !== 'isEmergencyState',
  });
}
